import React, { useEffect, useState, useRef, useCallback } from "react";
import { Bell } from "lucide-react";
import { api } from "../lib/api";

/**
 * Bell con badge "nuove attività dal mio ultimo refresh".
 *
 * Polling ogni 30s su `/admin/whats-new`. Confronta `total` con il valore "visto"
 * (salvato in sessionStorage). Mostra il delta come badge rosso pulsante.
 *
 * Click → riapre il modal dispatchando l'evento `archihub:open-admin-whatsnew`
 * (AdminWhatsNewModal lo ascolta e si riapre con i dati freschi).
 */
const SEEN_TOTAL_KEY = "ah_admin_seen_total";
const POLL_MS = 15000;

export default function AdminLiveBell({ user }) {
  const [newCount, setNewCount] = useState(0);
  const [lastFetchOk, setLastFetchOk] = useState(true);
  const seenRef = useRef(0);

  // Inizializza la baseline una sola volta (cosa l'admin ha già "visto" all'apertura della sessione corrente).
  // Se sessionStorage non ha nulla, usiamo 0 → la prima volta il badge potrebbe mostrare il totale corrente;
  // per evitare di "spaventare" l'admin con un numero alto, settiamo la baseline = valore corrente al primo fetch.
  const baselineInitedRef = useRef(false);

  const fetchOnce = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/whats-new");
      const totalActivities = data?.total || 0;
      const pendingReq = (data?.pending_discount_requests || []).length;
      const pendingMagic = (data?.pending_magic_search_requests || []).length;
      const pendingUsr = (data?.pending_users || []).length;
      const pendingContacts = (data?.pending_contacts || []).length;
      const submitted = (data?.submitted_projects || []).length;
      const total = totalActivities + pendingReq + pendingMagic + submitted + pendingUsr + pendingContacts;

      if (!baselineInitedRef.current) {
        baselineInitedRef.current = true;
        const stored = parseInt(sessionStorage.getItem(SEEN_TOTAL_KEY) || "", 10);
        if (Number.isNaN(stored)) {
          // Prima visita della sessione: nessuna novità da segnalare.
          seenRef.current = total;
          sessionStorage.setItem(SEEN_TOTAL_KEY, String(total));
        } else {
          seenRef.current = stored;
        }
      }
      const delta = Math.max(0, total - seenRef.current);
      setNewCount(delta);
      setLastFetchOk(true);
    } catch {
      setLastFetchOk(false);
    }
  }, []);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_MS);
    // Refetch immediato quando la tab torna in focus.
    const onFocus = () => fetchOnce();
    window.addEventListener("focus", onFocus);
    // Sync con la chiusura del WhatsNewModal: quando l'utente clicca "Ho capito" salviamo il nuovo baseline.
    const onSeen = (e) => {
      const totalSeen = e?.detail?.total;
      if (typeof totalSeen === "number") {
        seenRef.current = totalSeen;
        sessionStorage.setItem(SEEN_TOTAL_KEY, String(totalSeen));
        setNewCount(0);
      }
    };
    window.addEventListener("archihub:admin-whatsnew-seen", onSeen);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("archihub:admin-whatsnew-seen", onSeen);
    };
  }, [user, fetchOnce]);

  function openModal() {
    window.dispatchEvent(new CustomEvent("archihub:open-admin-whatsnew"));
  }

  if (!user || user.role !== "admin") return null;

  return (
    <button
      type="button"
      onClick={openModal}
      className="relative inline-flex items-center justify-center w-9 h-9 rounded-sm hover:bg-cream2 transition-colors"
      title={lastFetchOk
        ? (newCount > 0 ? `${newCount} nuove attività dal tuo ultimo refresh` : "Nessuna nuova attività")
        : "Offline · ricarica la pagina"}
      data-testid="admin-live-bell"
      data-count={newCount}
    >
      <Bell size={16} className={newCount > 0 ? "text-rose-700" : "text-slate1"} />
      {newCount > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 bg-rose-600 text-cream text-[9px] font-medium rounded-full min-w-[16px] h-4 px-1 inline-flex items-center justify-center animate-pulse"
          data-testid="admin-live-bell-count"
        >
          {newCount > 99 ? "99+" : newCount}
        </span>
      )}
    </button>
  );
}
